import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
  Divider,
  Slider,
} from '@mui/material';
import api from '../../utils/api';
import { logger } from '../../utils/logger';

interface AiCatalogingConfig {
  modelId: string;
  prompt: string;
  maxImagesPerBatch: number;
  maxZipSizeMb: number;
  enabled: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

const DEFAULT_CONFIG: AiCatalogingConfig = {
  modelId: 'amazon.nova-lite-v1:0',
  prompt: '',
  maxImagesPerBatch: 100,
  maxZipSizeMb: 500,
  enabled: true,
};

const AiCatalogingTab: React.FC = () => {
  const [config, setConfig] = useState<AiCatalogingConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/api/admin/ai-cataloging/config');
        setConfig(response.data);
      } catch (err) {
        logger.error('Failed to fetch AI cataloging config', err);
        setErrorMessage('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const response = await api.put('/api/admin/ai-cataloging/config', {
        modelId: config.modelId,
        prompt: config.prompt,
        maxImagesPerBatch: config.maxImagesPerBatch,
        maxZipSizeMb: config.maxZipSizeMb,
        enabled: config.enabled,
      });
      setConfig(response.data);
      setSuccessMessage('Configuration saved successfully');
    } catch (err) {
      logger.error('Failed to save AI cataloging config', err);
      setErrorMessage('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        AI Cataloging Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure the AI-powered artwork cataloging pipeline. Changes take effect within 60 seconds.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={config.enabled}
            onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
          />
        }
        label="Enable AI Cataloging"
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      <TextField
        label="Bedrock Model ID"
        fullWidth
        value={config.modelId}
        onChange={(e) => setConfig(prev => ({ ...prev, modelId: e.target.value }))}
        helperText="Amazon Bedrock model ID (e.g., amazon.nova-lite-v1:0)"
        sx={{ mb: 3 }}
      />

      <TextField
        label="Analysis Prompt"
        fullWidth
        multiline
        rows={6}
        value={config.prompt}
        onChange={(e) => setConfig(prev => ({ ...prev, prompt: e.target.value }))}
        helperText={`${config.prompt.length} / 5000 characters`}
        inputProps={{ maxLength: 5000 }}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Batch Limits
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          Max images per batch: {config.maxImagesPerBatch}
        </Typography>
        <Slider
          value={config.maxImagesPerBatch}
          onChange={(_, value) => setConfig(prev => ({ ...prev, maxImagesPerBatch: value as number }))}
          min={1}
          max={500}
          step={10}
          valueLabelDisplay="auto"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          Max ZIP file size: {config.maxZipSizeMb} MB
        </Typography>
        <Slider
          value={config.maxZipSizeMb}
          onChange={(_, value) => setConfig(prev => ({ ...prev, maxZipSizeMb: value as number }))}
          min={50}
          max={2000}
          step={50}
          valueLabelDisplay="auto"
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {config.updatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Last updated: {new Date(config.updatedAt).toLocaleString()}
          {config.updatedBy && ` by ${config.updatedBy}`}
        </Typography>
      )}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving}
        sx={{ mt: 1 }}
      >
        {saving ? <CircularProgress size={20} /> : 'Save Configuration'}
      </Button>
    </Box>
  );
};

export default AiCatalogingTab;
